import React, { useMemo } from 'react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import type { ProjectRole, RolePermission } from '../types';
import { RolePermissionsPanel } from './RolePermissionsPanel';

interface RoleDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: ProjectRole | null;
  /** Full project permission catalog (used to show included vs not included). */
  permissions: RolePermission[];
}

export const RoleDetailsDialog: React.FC<RoleDetailsDialogProps> = ({
  open,
  onOpenChange,
  role,
  permissions,
}) => {
  const includedPermissionIds = useMemo(
    () => role?.permissions.map((permission) => permission.id) ?? [],
    [role],
  );

  if (!role) {
    return null;
  }

  const includedCount = includedPermissionIds.length;
  const notIncludedCount = Math.max(permissions.length - includedCount, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{role.name}</DialogTitle>
          <DialogDescription>
            {includedCount} of {permissions.length} permissions included
            {notIncludedCount > 0 ? ` · ${notIncludedCount} not included` : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {role.is_admin && <Badge variant="destructive">Admin</Badge>}
            {role.is_default && <Badge variant="secondary">Default</Badge>}
            {!role.is_admin && !role.is_default && <Badge variant="outline">Custom</Badge>}
            <Badge variant="outline">{role.member_count} members</Badge>
          </div>

          <div className="space-y-2">
            <Label>Permissions</Label>
            <RolePermissionsPanel
              mode="view"
              permissions={permissions}
              includedPermissionIds={includedPermissionIds}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
